import { Button } from "@eventbrite/marmalade"
import classNames from "classnames"
import { FC, useCallback, useEffect, useMemo, useState } from "react"
import { useSelector } from "react-redux"

// components
import AudienceQuestionsSection from "./AudienceQuestionsSection"

// types
import { Props as AudienceQuestionsSectionProps } from "./AudienceQuestionsSection"
import { SessionStorageKeys } from "../../types/window_session_storage.enum"
import { RootState } from "../../store/store"
import { userProfileGuardCheck } from "../../store/slices/user_profile.slice"

// styles
import * as styles from "./CommonQuestions.module.scss"

const CommonQuestionsSection: FC<{
  data: Queries.CommonIssuesAndQuestionsQuery["contentfulLandingPage"]
  audienceQuestionsSectionMicroCopy: AudienceQuestionsSectionProps["audienceQuestionsSectionMicroCopy"]
  messageLoginContactUsMicroCopy: Queries.CommonIssuesAndQuestionsQuery["messageLoginContactUsMicroCopy"]
  chatProvisioned: boolean
  withinChatBusinessHours: boolean
}> = ({
  data,
  audienceQuestionsSectionMicroCopy,
  messageLoginContactUsMicroCopy,
  chatProvisioned,
  withinChatBusinessHours
}) => {
  const userProfile = userProfileGuardCheck(
    useSelector((state: RootState) => state.userProfile)
  )
  const isOrganizer = userProfile?.isOrganizer
  const hasAttemptedLogin = useSelector(
    (state: RootState) => state.metaData.hasAttemptedLogin
  )

  const findSegment = useCallback(
    (title: string) => {
      const segmentData = [
        data?.attending,
        data?.organizing,
        data?.somethingElse
      ]
      return segmentData.find((segment) =>
        segment?.sectionTitle?.includes(title)
      )
    },
    [data?.attending, data?.organizing, data?.somethingElse]
  )

  const initialSegment = useMemo(() => {
    if (typeof window !== "undefined" && window?.sessionStorage?.getItem) {
      const persistentSegmentTitle = window?.sessionStorage.getItem(
        SessionStorageKeys.contactUsAccordionSegmentTitle
      )

      if (persistentSegmentTitle && persistentSegmentTitle.length > 0) {
        return findSegment(persistentSegmentTitle)
      } else {
        return data?.default
      }
    } else {
      return data?.default
    }
  }, [])

  const [segment, setSegment] = useState(initialSegment)

  // Update segment when user profile loads (only if no session storage exists)
  useEffect(() => {
    const persistentSegmentTitle =
      typeof window !== "undefined"
        ? window?.sessionStorage?.getItem(
            SessionStorageKeys.contactUsAccordionSegmentTitle
          )
        : null
    if (
      hasAttemptedLogin &&
      !persistentSegmentTitle &&
      isOrganizer &&
      data?.organizing
    ) {
      setSegment(data.organizing)
    }
  }, [hasAttemptedLogin, isOrganizer, data?.organizing])

  const stripOne = (s?: string | null) =>
    s?.includes("Common issues and questions")
      ? s.split("Common issues and questions: ")[1]
      : s ?? ""

  const tabs = useMemo(
    () =>
      [
        { key: "attending", title: stripOne(data?.attending?.sectionTitle) },
        { key: "organizing", title: stripOne(data?.organizing?.sectionTitle) },
        {
          key: "somethingElse",
          title: stripOne(data?.somethingElse?.sectionTitle)
        }
      ].filter((t) => t.title),
    [data]
  )

  const clickHandler = useCallback((title: string) => {
    setHasUserInteracted(true)
    setSegment(findSegment(title))
  }, [])

  const [hasUserInteracted, setHasUserInteracted] = useState(false)

  useEffect(() => {
    if (typeof window !== "undefined") {
      // Only save to session storage after user has interacted or after user profile determines choice
      if (
        (hasUserInteracted || (hasAttemptedLogin && isOrganizer)) &&
        window?.sessionStorage?.setItem &&
        typeof segment?.sectionTitle === "string"
      ) {
        window?.sessionStorage?.setItem(
          SessionStorageKeys?.contactUsAccordionSegmentTitle,
          segment?.sectionTitle
        )
      }

      // on unmount of this accordion/navigation away from the page we want to clear the persistent data for the accordion (may need to look into other options here)
      return () => {
        window?.sessionStorage.removeItem(
          SessionStorageKeys.contactUsAccordionSegmentTitle
        )
      }
    }
  }, [segment, hasUserInteracted, hasAttemptedLogin, isOrganizer])

  return (
    <div className={styles?.commonQuestionsSection}>
      <h2 className={styles?.header}>{data?.title}</h2>
      <div className={styles?.tabSection}>
        {tabs.map(({ key, title }) => (
          <Button
            key={key}
            data-tour-step={
              key === "organizing" ? "contactus-organizer-tab" : undefined
            }
            className={classNames(styles?.tabs, {
              [styles?.tabSelected]: segment?.sectionTitle?.includes(title)
            })}
            onClick={() => clickHandler(title)}
          >
            {title}
          </Button>
        ))}
      </div>
      <AudienceQuestionsSection
        audience={segment ?? null}
        audienceQuestionsSectionMicroCopy={audienceQuestionsSectionMicroCopy}
        messageLoginContactUsMicroCopy={messageLoginContactUsMicroCopy}
        chatProvisioned={chatProvisioned}
        withinChatBusinessHours={withinChatBusinessHours}
      />
    </div>
  )
}

export default CommonQuestionsSection
